import { useEffect, useState } from 'react';
import { WatchlistApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { handleAdminError } from '../../common/SharedFunctions';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import LoadPanel from 'devextreme-react/load-panel';
import { formatDateMonthDayYear, formatCurrency } from '@apply/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

const AppraisalHistoryTable = ({ loanId, propertyId }) => {
    const [data, set_data] = useState([]);
    const [loadingMessage, set_loadingMessage] = useState('');
    const watchlistApi = new WatchlistApi.WatchlistApi(applicationInsights);

    const styles = {
        iconYes: { color: 'mediumblue', cursor: 'pointer' },
    };

    const formatDate = (dt) => {
        if (!dt) {
            return null;
        }
        return formatDateMonthDayYear(dt);
    };

    const onDateRender = (e) => {
        return formatDate(e.value);
    };

    const onCurrencyRender = (e) => {
        return formatCurrency(e.value, 0);
    };

    const loadHistoryAsync = async (loanId, propertyId) => {
        try {
            set_loadingMessage('Retrieving history...');
            if (propertyId) {
                const response = await watchlistApi.getAppraisalsHistoryByProperty(propertyId);
                if (response.statusCode !== 200) {
                    throw new Error('Failed to retrieve property appraisal history data!');
                }
                const records = response.result;
                set_data(records);
            } else if (loanId) {
                const response = await watchlistApi.getAppraisalsHistoryByLoan(loanId);
                if (response.statusCode !== 200) {
                    throw new Error('Failed to retrieve loan appraisal history data!');
                }
                const records = response.result;
                set_data(records);
            } else {
                throw new Error('Failed to load appraisal history - no item information provided.');
            }
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve records! Please refresh the page and try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const renderPercent = (val, precision) => {
        if (!val) {
            return null;
        }
        return val.toFixed(precision) + '%';
    };

    const onPercentRender = (e, precision = 2) => {
        if (isNaN(!e.value)) {
            return null;
        }
        return renderPercent(e.value, precision);
    };

    const onFileRender = (e) => {
        if (!e.value) {
            return 'No File found';
        }
        return (
            <>
                <a
                    href={e.value}
                    target='_blank'
                    rel='noreferrer'
                    title='Download the file'
                >
                    <FontAwesomeIcon
                        icon={faDownload}
                        style={styles.iconYes}
                    />
                    &nbsp;&nbsp;{e.data.fileName}
                </a>
            </>
        );
    };

    useEffect(() => {
        if (!loanId && !propertyId) {
            return;
        }

        loadHistoryAsync(loanId, propertyId);
    }, [loanId, propertyId]);

    if (!loanId && !propertyId) {
        return null;
    }

    return (
        <>
            <DataGrid
                keyExpr={'id'}
                dataSource={data}
                rowAlternationEnabled={true}
                showBorders={true}
                showColumnHeaders={true}
                showRowLines={true}
                wordWrapEnabled={true}
                noDataText={'No Appraisal history found for this loan'}
            >
                <Column
                    dataField='appraisedDate'
                    caption='Date'
                    width={100}
                    cellRender={onDateRender}
                    sortIndex={0}
                    sortOrder={'desc'}
                />
                <Column
                    dataField='appraisedValue'
                    caption='Value'
                    width={100}
                    alignment='left'
                    cellRender={onCurrencyRender}
                />
                <Column
                    dataField={'ltv'}
                    caption={propertyId ? 'Property LTV' : 'Loan LTV'}
                    cellRender={(e) => onPercentRender(e, 1)}
                    width={100}
                    alignment='left'
                />
                <Column
                    dataField={'url'}
                    caption={'Report'}
                    cellRender={onFileRender}
                />
                {/* <Column dataField="userName" caption="Changed By" alignment="left" /> */}
            </DataGrid>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default AppraisalHistoryTable;
